/**
 * Variant B's word diff, computed in the browser on every render
 * (`decisions/the-variant-b-diff-is-computed-in-the-browser.md`). A hand-rolled
 * case-insensitive LCS over words -- deliberately not `DiffSegment`
 * (`types/listing.ts`) or `DiffText`, which segment a different kind of text
 * at pull time and are styled differently. Do not import either here.
 *
 * Whitespace travels with the word before it, so `same + added` reconstructs
 * `b` byte for byte and `same + removed` reconstructs `a` byte for byte --
 * except where a `same` segment's casing differs between `a` and `b`, where
 * it deliberately shows `b`'s own casing (decision 2).
 */

export type WordDiffSegment = {
  text: string;
  type: "same" | "added" | "removed";
};

type Token = {
  /** Lower-cased word, used only for the case-insensitive comparison. Empty for the leading-whitespace pseudo-token. */
  key: string;
  /** The word's own text, original casing, no whitespace. Empty for the leading-whitespace pseudo-token. */
  word: string;
  /** The whitespace that trails this word in the source string (possibly empty). */
  ws: string;
};

/**
 * Splits `text` into words, each carrying its own trailing whitespace, so
 * that concatenating every token's `word + ws` in order reconstructs `text`
 * exactly. Leading whitespace (rare for a title or bullet, but not
 * impossible) becomes its own pseudo-token so the invariant holds even then.
 */
function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  let start = 0;

  const leadingWhitespace = text.match(/^\s+/);
  if (leadingWhitespace) {
    tokens.push({ key: "", word: "", ws: leadingWhitespace[0] });
    start = leadingWhitespace[0].length;
  }

  const wordRe = /\S+\s*/g;
  wordRe.lastIndex = start;
  let match: RegExpExecArray | null;
  while ((match = wordRe.exec(text)) !== null) {
    const raw = match[0];
    const trailingWhitespace = raw.match(/\s+$/);
    const ws = trailingWhitespace ? trailingWhitespace[0] : "";
    const word = ws ? raw.slice(0, raw.length - ws.length) : raw;
    tokens.push({ key: word.toLowerCase(), word, ws });
  }

  return tokens;
}

type RawOp = { type: WordDiffSegment["type"]; text: string };

/**
 * A hand-rolled LCS over word tokens. `a` and `b` are capped at a few hundred
 * characters (title / bullet), so the O(n·m) table is small.
 */
export function diffWords(a: string, b: string): WordDiffSegment[] {
  const tokensA = tokenize(a);
  const tokensB = tokenize(b);
  const n = tokensA.length;
  const m = tokensB.length;

  // dp[i][j] = length of the LCS of tokensA[i:] and tokensB[j:], matching on
  // the case-insensitive word only -- never on whitespace, or an incidental
  // shared space between two otherwise-different words would let the LCS
  // "match" through it and fragment a change region (see the coalescing
  // requirement below).
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        tokensA[i].key === tokensB[j].key
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const rawOps: RawOp[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (tokensA[i].key === tokensB[j].key) {
      const wordText = tokensB[j].word;
      if (tokensA[i].ws === tokensB[j].ws) {
        // The common case: the matched word carries the same trailing
        // whitespace on both sides, so one `same` op reconstructs both.
        rawOps.push({ type: "same", text: wordText + tokensA[i].ws });
      } else {
        // The word matches but what follows it differs (typically: this is
        // the last word before a deletion/insertion on one side only). A
        // single `same` op cannot carry two different trailing whitespaces
        // at once, so the word goes out bare and each side's own trailing
        // whitespace goes out as its own removed/added sliver -- it still
        // coalesces into the surrounding change region below.
        rawOps.push({ type: "same", text: wordText });
        if (tokensA[i].ws) rawOps.push({ type: "removed", text: tokensA[i].ws });
        if (tokensB[j].ws) rawOps.push({ type: "added", text: tokensB[j].ws });
      }
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      rawOps.push({ type: "removed", text: tokensA[i].word + tokensA[i].ws });
      i++;
    } else {
      rawOps.push({ type: "added", text: tokensB[j].word + tokensB[j].ws });
      j++;
    }
  }
  while (i < n) {
    rawOps.push({ type: "removed", text: tokensA[i].word + tokensA[i].ws });
    i++;
  }
  while (j < m) {
    rawOps.push({ type: "added", text: tokensB[j].word + tokensB[j].ws });
    j++;
  }

  // Coalesce: a run of consecutive same-type ops becomes one segment, and a
  // run of mixed removed/added ops between two `same` runs always renders as
  // one `removed` segment (in a's order) followed by one `added` segment (in
  // b's order), regardless of the order the backtrack above visited them in.
  const segments: WordDiffSegment[] = [];
  let sameBuffer = "";
  let removedBuffer = "";
  let addedBuffer = "";

  const flushChangeRun = () => {
    if (removedBuffer) {
      segments.push({ type: "removed", text: removedBuffer });
      removedBuffer = "";
    }
    if (addedBuffer) {
      segments.push({ type: "added", text: addedBuffer });
      addedBuffer = "";
    }
  };
  const flushSameRun = () => {
    if (sameBuffer) {
      segments.push({ type: "same", text: sameBuffer });
      sameBuffer = "";
    }
  };

  for (const op of rawOps) {
    if (op.type === "same") {
      flushChangeRun();
      sameBuffer += op.text;
    } else {
      flushSameRun();
      if (op.type === "removed") {
        removedBuffer += op.text;
      } else {
        addedBuffer += op.text;
      }
    }
  }
  flushSameRun();
  flushChangeRun();

  return segments;
}

/** True iff every segment is `same` -- the one verdict `No changes yet` uses. */
export const isUnchanged = (segments: WordDiffSegment[]): boolean =>
  segments.every((segment) => segment.type === "same");
